import React from "react";
import {
  Box,
  Stack,
  Typography,
  Skeleton,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MessageSquare, Plus, Search, SquarePen, CheckCircle2, X } from "lucide-react";
import { Button } from "../../components/Button";
import { EmptyStateMessage } from "../../components/EmptyStateMessage";
import { CustomTextArea } from "../../components/CustomTextArea";
import { UnreadThreadBadge } from "../../components/UnreadThreadBadge";

export function ThreadListView({
  threads = [],
  selectedThreadId,
  selectedMessageThreadId = null,
  onThreadSelect,
  onCreateThread,
  isLoading = false,
  canCreateThreads = false,
  clientId,
  onSearchThread,
  isClientView = false,
  onUpdateThreadTitle,
}) {
  const theme = useTheme();
  const [localThreads, setLocalThreads] = React.useState(threads);

  React.useEffect(() => {
    setLocalThreads(threads);
  }, [threads]);

  const handleTitleUpdate = React.useCallback(
    async (threadId, newTitle, options = {}) => {
      if (!onUpdateThreadTitle) return null;

      const result = await onUpdateThreadTitle({
        threadId,
        clientId,
        title: newTitle,
        isMessageThread: !!options?.isMessageThread,
      });

      const resolvedTitle =
        result?.title ?? result?.is_thread_title ?? newTitle;
      if (resolvedTitle) {
        setLocalThreads((prevThreads = []) =>
          prevThreads.map((item) =>
            String(item.id) === String(threadId)
              ? { ...item, title: resolvedTitle }
              : item
          )
        );
      }

      return result;
    },
    [onUpdateThreadTitle, clientId]
  );

  if (isLoading) {
    return (
      <Box sx={{ p: "12px" }}>
        <Stack spacing={0.75}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Box
              key={`thread-skeleton-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                padding: "12px 14px",
                borderRadius: "8px",
              }}
            >
              <Skeleton variant="circular" width={24} height={24} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="70%" height={18} />
                <Skeleton variant="text" width="50%" height={16} />
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Stack spacing={0}>
      {/* Contenedor de threads con padding y gap */}
      <Box sx={{ p: "12px" }}>
        <Stack spacing={0.75}>
          {localThreads.map((thread) => {
            const isMessageThread = !!thread?.is_message_thread;
            const isSelected = isMessageThread
              ? String(selectedMessageThreadId) === String(thread.id)
              : String(selectedThreadId) === String(thread.id) &&
                !selectedMessageThreadId;
            return (
              <ThreadListRow
                key={
                  thread?.is_message_thread
                    ? `message-thread-${thread.id}`
                    : `thread-${thread.id}`
                }
                thread={thread}
                isSelected={isSelected}
                theme={theme}
                showLeadingIcon={!isClientView}
                showMessageCount={!isClientView}
                onSelect={() => {
                  const targetThreadId = thread?.is_message_thread
                    ? thread?.parent_thread_id
                    : thread?.id;
                  onThreadSelect(clientId, targetThreadId, {
                    thread,
                    messageThreadId: thread?.is_message_thread ? thread?.id : null,
                    messageThreadTitle: thread?.is_message_thread
                      ? thread?.title || ""
                      : "",
                  });
                }}
                onSearchThread={!isClientView ? onSearchThread : undefined}
                canEdit={canCreateThreads}
                onEditThreadTitle={handleTitleUpdate}
                unreadCount={isClientView ? (thread.unopened_unread_notifications || 0) : 0}
              />
            );
          })}
        </Stack>
      </Box>

      {/* Botón para crear nuevo thread */}
      {canCreateThreads && onCreateThread && !isClientView && (
        <Box
          sx={{
            padding: "0 12px 12px 12px",
          }}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={onCreateThread}
            sx={{
              width: "100%",
              justifyContent: "flex-start",
              color: theme.palette.text.secondary,
              fontSize: "0.85rem",
              py: 0.75,
              "&:hover": {
                color: theme.palette.primary.main,
              },
            }}
          >
            <Plus size={14} style={{ marginRight: 6 }} />
            temático
          </Button>
        </Box>
      )}

      {localThreads.length === 0 && (
        <Box sx={{ padding: "64px 32px" }}>
          <EmptyStateMessage
            icon={MessageSquare}
            message="No hay conversaciones"
          />
        </Box>
      )}
    </Stack>
  );
}

function ThreadListRow({
  thread,
  isSelected,
  theme,
  showLeadingIcon,
  onSelect,
  onSearchThread,
  showMessageCount,
  canEdit,
  onEditThreadTitle,
  unreadCount = 0,
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [titleValue, setTitleValue] = React.useState(
    thread.title ||
      (thread.type === "general" ? "General" : "")
  );
  const [error, setError] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const isGeneralThread = thread.type === "general" || !!thread.is_general;
  const isMessageThread = !!thread?.is_message_thread;
  const canEditThread = canEdit && !isGeneralThread;

  const displayTitle = isGeneralThread
    ? thread.title?.trim() || "General"
    : thread.title?.trim() || "Sin título";

  React.useEffect(() => {
    if (!isEditing) {
      setTitleValue(
        thread.title || (thread.type === "general" ? "General" : "")
      );
    }
  }, [thread.title, thread.type, isEditing]);

  const handleRowClick = (event) => {
    if (isEditing) {
      event.stopPropagation();
      return;
    }
    onSelect();
  };

  const handleEditStart = (event) => {
    event.stopPropagation();
    if (!canEditThread) return;
    setError("");
    setIsEditing(true);
  };

  const handleCancel = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setIsEditing(false);
    setError("");
    setTitleValue(
      thread.title || (thread.type === "general" ? "General" : "")
    );
  };

  const handleSave = async (event) => {
    if (event) {
      event.stopPropagation();
    }

    if (!canEditThread) {
      return;
    }

    const trimmedTitle = titleValue.trim();

    if (!trimmedTitle) {
      setError("El título no puede estar vacío.");
      return;
    }

    if (trimmedTitle.length < 3) {
      setError("El título debe tener al menos 3 caracteres.");
      return;
    }

    if (trimmedTitle === thread.title) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      await onEditThreadTitle?.(thread.id, trimmedTitle, { isMessageThread });
      setIsEditing(false);
      setError("");
    } catch (saveError) {
      const errorMessage =
        saveError?.message || "No se pudo actualizar el título.";
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      onClick={handleRowClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        padding: "12px 14px",
        cursor: "pointer",
        borderRadius: "8px",
        backgroundColor: isSelected
          ? theme.palette.action.selected
          : "transparent",
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
        "&:hover .thread-list-search-btn, &:hover .thread-list-edit-btn": {
          opacity: 1,
          transform: "translateX(0)",
        },
      }}
    >
      {showLeadingIcon && (
        <MessageSquare
          size={16}
          color={theme.palette.text.secondary}
          style={{ flexShrink: 0 }}
        />
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {isEditing && canEditThread ? (
          <>
            <CustomTextArea
              value={titleValue}
              onChange={(event) => {
                setTitleValue(event.target.value);
                if (error) {
                  setError("");
                }
              }}
              rows={1}
              maxRows={3}
              autoExpand
              placeholder="Nuevo título"
              autoFocus
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  handleSave(event);
                }
                if (event.key === "Escape") {
                  event.preventDefault();
                  handleCancel(event);
                }
              }}
              sx={{ fontSize: "0.9rem", minHeight: "36px" }}
            />
            {error && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.error.main,
                  display: "block",
                  marginTop: "4px",
                }}
                onClick={(event) => event.stopPropagation()}
              >
                {error}
              </Typography>
            )}
          </>
        ) : (
          <>
            <Tooltip title={displayTitle} arrow enterDelay={250}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isSelected ? 600 : 500,
                  fontSize: "0.9rem",
                  color: theme.palette.text.primary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayTitle}
              </Typography>
            </Tooltip>
            {showMessageCount && thread.message_count > 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  opacity: 0.7,
                  fontSize: "0.75rem",
                }}
              >
                {thread.message_count} mensaje
                {thread.message_count !== 1 ? "s" : ""}
              </Typography>
            )}
          </>
        )}
      </Box>

      {isEditing && canEditThread ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Tooltip title="Guardar título" arrow enterDelay={150}>
            <IconButton
              size="small"
              onClick={handleSave}
              disabled={isSaving}
              sx={{
                padding: "4px",
                color: theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {isSaving ? (
                <CircularProgress size={14} />
              ) : (
                <CheckCircle2 size={16} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancelar edición" arrow enterDelay={150}>
            <IconButton
              size="small"
              onClick={handleCancel}
              sx={{
                padding: "4px",
                color: theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <X size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <>
          {unreadCount > 0 && !isSelected && (
            <UnreadThreadBadge count={unreadCount} />
          )}
          {onSearchThread && (
            <Tooltip title="Buscar mensajes en este hilo" arrow enterDelay={150}>
              <Button
                variant="ghost"
                size="icon"
                className="thread-list-search-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  onSearchThread(thread);
                }}
                sx={{
                  opacity: 0,
                  transform: "translateX(8px)",
                  transition: "all 0.2s ease",
                  color: theme.palette.text.secondary,
                  minWidth: "32px",
                  width: "32px",
                  height: "32px",
                  borderRadius: "999px",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <Search size={16} />
              </Button>
            </Tooltip>
          )}
          {canEditThread && (
            <Tooltip title="Editar título" arrow enterDelay={150}>
              <Button
                variant="ghost"
                size="icon"
                className="thread-list-edit-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  handleEditStart(event);
                }}
                sx={{
                  opacity: 0,
                  transform: "translateX(8px)",
                  transition: "all 0.2s ease",
                  color: theme.palette.text.secondary,
                  minWidth: "32px",
                  width: "32px",
                  height: "32px",
                  borderRadius: "999px",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <SquarePen size={16} />
              </Button>
            </Tooltip>
          )}
        </>
      )}
    </Box>
  );
}
